// src/pages/client-management/index.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumbs from "../../components/ui/Breadcrumbs";

import ClientCard from "./components/ClientCard";
import ClientFilters from "./components/ClientFilters";
import ClientForm from "./components/ClientForm";
import ClientStats from "./components/ClientStats";
import OverlayListModal from "../../components/ui/OverlayListModal";
import AppointmentBookingModal from "./components/AppointmentBookingModal";
import ClientTable from "./components/ClientTable";
import ClientProfileDrawer from "./components/ClientProfileDrawer";

import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import api from "../../lib/api";

import { useAuth } from "../../auth/AuthProvider";
import { listClients, createClient, updateClient, importClients } from "../../lib/clients";

import Papa from "papaparse";

/* ===========================
   Helpers export / import
=========================== */

const escapeXml = (value) =>
  String(value ?? "").replace(/[<>&'"]/g, (c) => {
    switch (c) {
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case "&":
        return "&amp;";
      case "'":
        return "&apos;";
      case '"':
        return "&quot;";
      default:
        return c;
    }
  });

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const todaySlug = () => new Date().toISOString().slice(0, 10);

const clientToRow = (c) => ({
  name: c?.name || "",
  email: c?.email || "",
  phone: c?.phone || "",
  status: c?.status || "",
  lastVisit: c?.lastVisit || "",
  totalAppointments: c?.totalAppointments ?? 0,
  totalSpent: c?.totalSpent ?? 0,
  loyaltyPoints: c?.loyaltyPoints ?? 0,
});

const rowToClientPayload = (row) => {
  const statusRaw = (row.status || row.Status || "active").toString().toLowerCase();
  const allowedStatus = ["active", "inactive", "vip"];
  const status = allowedStatus.includes(statusRaw) ? statusRaw : "active";

  return {
    name:
      row.name ||
      row.Name ||
      `${row.first_name || ""} ${row.last_name || ""}`.trim(),
    email: row.email || row.Email || row["e-mail"] || row.mail || "",
    phone: row.phone || row.Phone || row.telefon || row.tel || row.mobile || "",
    status,
    lastVisit: row.lastVisit || row.LastVisit || "",
  };
};

const parseCsvClients = (text) => {
  const res = Papa.parse(text, {
    header: true,
    skipEmptyLines: "greedy",
    delimitersToGuess: [",", ";", "\t", "|"],
    transformHeader: (h) =>
      String(h || "")
        .replace(/^\uFEFF/, "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "_"),
    transform: (v) => (typeof v === "string" ? v.trim() : v),
  });

  if (res.errors?.length) {
    const first = res.errors[0];
    throw new Error(
      `CSV invalid: ${first?.message || "Eroare de parsare"} (rând ${first?.row ?? "?"}).`
    );
  }

  const rows = [];
  for (const raw of res.data || []) {
    const payload = rowToClientPayload(raw);
    if (payload.name || payload.phone || payload.email) rows.push(payload);
  }
  return rows;
};

const parseXmlClients = (text) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, "application/xml");

  // DOMParser pune <parsererror> dacă XML-ul e invalid
  if (doc.getElementsByTagName("parsererror")?.length) {
    throw new Error("XML invalid: fișierul nu poate fi interpretat.");
  }

  // XML este case-sensitive -> suportăm și <Client>, <CLIENT>, etc.
  const all = Array.from(doc.getElementsByTagName("*"));
  const clientNodes = all.filter((n) => String(n.localName || n.nodeName || "").toLowerCase() === "client");

  const getTextCI = (node, tag) => {
    const t = String(tag || "").toLowerCase();
    const kids = Array.from(node.getElementsByTagName("*"));
    const el = kids.find((x) => String(x.localName || x.nodeName || "").toLowerCase() === t);
    return (el?.textContent || "").trim();
  };

  const rows = [];
  clientNodes.forEach((node) => {
    const firstName =
      getTextCI(node, "first_name") ||
      getTextCI(node, "firstname") ||
      getTextCI(node, "firstName");

    const lastName =
      getTextCI(node, "last_name") ||
      getTextCI(node, "lastname") ||
      getTextCI(node, "lastName");

    const payload = rowToClientPayload({
      name: getTextCI(node, "name") || `${firstName} ${lastName}`.trim(),
      first_name: firstName,
      last_name: lastName,
      email: getTextCI(node, "email") || getTextCI(node, "mail") || getTextCI(node, "e-mail"),
      phone: getTextCI(node, "phone") || getTextCI(node, "telefon") || getTextCI(node, "mobile") || getTextCI(node, "tel"),
      status: getTextCI(node, "status"),
      lastVisit: getTextCI(node, "lastVisit") || getTextCI(node, "last_visit"),
    });

    if (payload.name || payload.phone || payload.email) rows.push(payload);
  });

  return rows;
};

/* ===========================
   Import report modal
=========================== */

const ImportReportModal = ({ isOpen, report, onClose }) => {
  if (!isOpen || !report) return null;

  const errs = Array.isArray(report.errors) ? report.errors : [];

  const downloadErrorsCsv = () => {
    if (!errs.length) return;

    const header = ["row", "field", "message", "raw"];
    const lines = [header.join(",")];

    errs.forEach((e) => {
      const row = e?.row ?? "";
      const field = String(e?.field ?? "").replace(/"/g, '""');
      const message = String(e?.message ?? "").replace(/"/g, '""');
      const raw = JSON.stringify(e?.raw ?? {}).replace(/"/g, '""');
      const values = [row, field, message, raw].map((v) => `"${String(v)}"`);
      lines.push(values.join(","));
    });

    const blob = new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    downloadBlob(blob, `import-errors-${report.filename || "clients"}.csv`);
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-start justify-center pt-20 pb-8 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-xl border border-border bg-background shadow-xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-lg font-semibold">Rezultat import</div>
            {report.filename ? (
              <div className="text-sm text-muted-foreground mt-1">Fișier: {report.filename}</div>
            ) : null}
          </div>

          <button
            type="button"
            className="p-2 rounded-md hover:bg-muted"
            onClick={onClose}
            aria-label="Închide"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="rounded-lg border border-border p-3">
            <div className="text-xs text-muted-foreground">Created</div>
            <div className="text-xl font-semibold">{report.created ?? 0}</div>
          </div>
          <div className="rounded-lg border border-border p-3">
            <div className="text-xs text-muted-foreground">Updated</div>
            <div className="text-xl font-semibold">{report.updated ?? 0}</div>
          </div>
          <div className="rounded-lg border border-border p-3">
            <div className="text-xs text-muted-foreground">Skipped (duplicate în fișier)</div>
            <div className="text-xl font-semibold">{report.skipped_duplicates_in_file ?? 0}</div>
          </div>
          <div className="rounded-lg border border-border p-3">
            <div className="text-xs text-muted-foreground">Invalid rows</div>
            <div className="text-xl font-semibold">{report.invalid_rows ?? 0}</div>
          </div>
        </div>

        {errs.length ? (
          <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/5 p-3">
            <div className="text-sm font-medium text-red-600">Au fost {errs.length} erori</div>
            <div className="text-xs text-muted-foreground mt-1">
              Poți descărca CSV cu rândurile care n-au mers.
            </div>

            <div className="mt-3 flex gap-2">
              <Button variant="outline" onClick={downloadErrorsCsv} iconName="Download" iconPosition="left">
                Descarcă erori CSV
              </Button>
              <Button variant="default" onClick={onClose}>OK</Button>
            </div>
          </div>
        ) : (
          <div className="mt-4 flex justify-end">
            <Button variant="default" onClick={onClose}>OK</Button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ===========================
   Main component
=========================== */

const ClientManagement = () => {
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [showClientForm, setShowClientForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);

  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  const [profileClient, setProfileClient] = useState(null);

  const [clients, setClients] = useState([]);
  const [showClientListModal, setShowClientListModal] = useState(false);

  const [isLoading, setIsLoading] = useState(false);   // submit formular
  const [isFetching, setIsFetching] = useState(false); // load listă
  const [isImporting, setIsImporting] = useState(false);

  const [viewMode, setViewMode] = useState("cards");

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name-asc");

  const [showExportMenu, setShowExportMenu] = useState(false);
  const fileInputRef = useRef(null);

  const { currentBusinessId, permissions } = useAuth();

  // Import report modal
  const [importReport, setImportReport] = useState(null);
  const [isImportReportOpen, setIsImportReportOpen] = useState(false);

  const baseStats = useMemo(
    () => ({
      totalClients: 0,
      activeClients: 0,
      newClientsThisMonth: 0,
      vipClients: 0,
      totalRevenue: 0,
      averageSpending: 0,
      clientsChange: 0,
      activeClientsChange: 0,
      newClientsChange: 0,
      vipClientsChange: 0,
      revenueChange: 0,
      avgSpendingChange: 0,
    }),
    []
  );

  const computedStats = useMemo(() => {
    if (!clients?.length) return baseStats;

    const totalClients = clients.length;
    const activeClients = clients.filter((c) => c.status === "active" || c.status === "vip").length;
    const vipClients = clients.filter((c) => c.status === "vip").length;

    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);

    const newClientsThisMonth = clients.filter((c) => {
      const rawCreated = c.createdAt || c.created_at || c._raw?.created_at;
      if (!rawCreated) return false;
      const d = new Date(rawCreated);
      return d >= monthStart && d < nextMonth;
    }).length;

    const totalRevenue = clients.reduce((sum, c) => sum + (Number(c.totalSpent) || 0), 0);
    const averageSpending = totalClients ? totalRevenue / totalClients : 0;

    return {
      ...baseStats,
      totalClients,
      activeClients,
      vipClients,
      newClientsThisMonth,
      totalRevenue,
      averageSpending,
    };
  }, [clients, baseStats]);

  const canSeeDashboard = Boolean(permissions?.canSeeDashboard);
  const canSeeStats = Boolean(permissions?.canSeeStats);

  const breadcrumbItems = canSeeDashboard
    ? [
        { label: t("nav.dashboard"), path: "/app/admin-dashboard" },
        { label: t("clients.title"), path: "/app/client-management" },
      ]
    : [{ label: t("clients.title"), path: "/app/client-management" }];

  // Load clients
  useEffect(() => {
    if (!currentBusinessId) return;

    let cancelled = false;

    const load = async () => {
      setIsFetching(true);
      try {
        const data = await listClients({ businessId: currentBusinessId });
        if (!cancelled) setClients(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Nu s-au putut încărca clienții:", e?.response?.data || e);
      } finally {
        if (!cancelled) setIsFetching(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [currentBusinessId]);

  const filteredAndSortedClients = useMemo(() => {
    const filtered = (clients || []).filter((client) => {
      const q = (searchTerm || "").toLowerCase();

      const matchesSearch =
        !q ||
        (client?.name || "").toLowerCase().includes(q) ||
        (client?.email || "").toLowerCase().includes(q) ||
        String(client?.phone || "").includes(searchTerm);

      const matchesStatus = statusFilter === "all" || client?.status === statusFilter;

      const matchesService =
        serviceFilter === "all" ||
        client?.preferredServices?.some((service) =>
          String(service || "").toLowerCase().includes(serviceFilter.replace("-", " ").toLowerCase())
        );

      return matchesSearch && matchesStatus && matchesService;
    });

    filtered.sort((a, b) => {
      switch (sortBy) {
        case "name-asc":
          return (a?.name || "").localeCompare(b?.name || "");
        case "name-desc":
          return (b?.name || "").localeCompare(a?.name || "");
        case "last-visit-desc":
          return new Date(b?.lastVisit || 0) - new Date(a?.lastVisit || 0);
        case "last-visit-asc":
          return new Date(a?.lastVisit || 0) - new Date(b?.lastVisit || 0);
        case "total-spent-desc":
          return (b?.totalSpent || 0) - (a?.totalSpent || 0);
        case "total-spent-asc":
          return (a?.totalSpent || 0) - (b?.totalSpent || 0);
        case "appointments-desc":
          return (b?.totalAppointments || 0) - (a?.totalAppointments || 0);
        case "appointments-asc":
          return (a?.totalAppointments || 0) - (b?.totalAppointments || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [clients, searchTerm, statusFilter, serviceFilter, sortBy]);

  const handleToggleSidebar = () => setSidebarCollapsed((v) => !v);

  const handleCreateClient = () => {
    setEditingClient(null);
    setShowClientForm(true);
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setShowClientForm(true);
  };

  const handleViewProfile = (client) => setProfileClient(client);

  const handleBookAppointment = (client) => {
    setSelectedClient(client);
    setShowBookingModal(true);
    setProfileClient(null);
  };

  const handleCloseClientForm = () => {
    setShowClientForm(false);
    setEditingClient(null);
  };

  const handleClientFormOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleCloseClientForm();
  };

  const handleSubmitClient = async (clientData) => {
    if (!currentBusinessId) return;

    if (clientData.phone && String(clientData.phone).trim()) {
      const digitsOnly = String(clientData.phone).replace(/\D/g, "");
      if (digitsOnly.length < 6 || digitsOnly.length > 15) {
        alert("Numărul de telefon trebuie să aibă între 6 și 15 cifre.");
        return;
      }
    }

    setIsLoading(true);
    try {
      if (editingClient) {
        const updated = await updateClient(editingClient.id, clientData, { businessId: currentBusinessId });
        setClients((prev) => prev.map((c) => (String(c.id) === String(updated.id) ? updated : c)));
      } else {
        const created = await createClient(clientData, { businessId: currentBusinessId });
        setClients((prev) => [...prev, created]);
      }
      setShowClientForm(false);
      setEditingClient(null);
    } catch (error) {
      console.error("Error saving client:", error?.response?.data || error);
    } finally {
      setIsLoading(false);
    }
  };

  const resolveLocationId = async () => {
    const raw =
      window.localStorage.getItem("current_location_id") ||
      window.localStorage.getItem("current_location_scope_id");

    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return n;

    try {
      const { data } = await api.get("/b/locations", {
        headers: { "X-Business-Id": String(currentBusinessId) },
      });

      const arr = Array.isArray(data)
        ? data
        : Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data?.items)
        ? data.items
        : [];

      const first = arr?.[0];
      const id = Number(first?.id);

      if (Number.isFinite(id) && id > 0) {
        window.localStorage.setItem("current_location_id", String(id));
        return id;
      }
    } catch (e) {
      console.error("Nu am putut încărca locațiile:", e?.response?.data || e);
    }

    return null;
  };

  const handleBookingSubmit = async (appointmentData) => {
    if (!currentBusinessId) return;

    let locationId = Number(
      appointmentData?.locationId ||
        window.localStorage.getItem("current_location_id") ||
        window.localStorage.getItem("current_location_scope_id")
    );

    if (!Number.isFinite(locationId) || locationId <= 0) locationId = await resolveLocationId();

    if (!Number.isFinite(locationId) || locationId <= 0) {
      alert("Nu există nicio locație disponibilă. Creează o locație sau selectează una.");
      return;
    }

    const rawServiceIds = Array.isArray(appointmentData?.serviceIds)
      ? appointmentData.serviceIds
      : appointmentData?.serviceId
      ? [appointmentData.serviceId]
      : [];

    const serviceIds = rawServiceIds.map((x) => Number(x)).filter((n) => Number.isFinite(n));
    if (!serviceIds.length) {
      alert("Selectează cel puțin un serviciu.");
      return;
    }

    const staffId = Number(appointmentData?.staffId ?? appointmentData?.staff ?? "");
    if (!Number.isFinite(staffId) || !staffId) {
      alert("Selectează un specialist.");
      return;
    }

    const startISO =
      appointmentData?.startISO ||
      (appointmentData?.date && appointmentData?.time
        ? new Date(`${appointmentData.date}T${appointmentData.time}`).toISOString()
        : null);

    if (!startISO) {
      alert("Selectează data și ora.");
      return;
    }

    const body = {
      start: startISO,
      status: "booked",
      notes: appointmentData?.notes || undefined,
      service: serviceIds[0],
      services: serviceIds,
      staff: staffId,
      client_id: Number(appointmentData?.clientId || selectedClient?.id),
      location_id: Number(locationId),
    };

    try {
      await api.post(`/b/appointments`, body, {
        headers: { "X-Business-Id": String(currentBusinessId) },
      });

      const refreshed = await listClients({ businessId: currentBusinessId });
      setClients(Array.isArray(refreshed) ? refreshed : []);

      setShowBookingModal(false);
      setSelectedClient(null);
      window.dispatchEvent(new CustomEvent("appointments:refetch"));
    } catch (err) {
      console.error("Eroare la creare programare:", err?.response?.data || err);
      const msg =
        err?.response?.data?.detail ||
        "Nu am putut crea programarea. Verifică datele și încearcă din nou.";
      alert(msg);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setServiceFilter("all");
    setSortBy("name-asc");
  };

  const handleExport = (format) => {
    const rows = (filteredAndSortedClients || []).map(clientToRow);
    if (!rows.length) return;

    const dateSlug = todaySlug();

    if (format === "csv") {
      const header = ["name", "email", "phone", "status", "lastVisit", "totalAppointments", "totalSpent", "loyaltyPoints"];
      const lines = [header.join(",")];

      rows.forEach((r) => {
        const values = header.map((key) => {
          const v = r[key] ?? "";
          const s = String(v).replace(/"/g, '""');
          return `"${s}"`;
        });
        lines.push(values.join(","));
      });

      downloadBlob(new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8;" }), `clients-${dateSlug}.csv`);
    }

    if (format === "xml") {
      let xml = '<?xml version="1.0" encoding="UTF-8"?>\n<clients>\n';
      rows.forEach((r) => {
        xml += "  <client>\n";
        xml += `    <name>${escapeXml(r.name)}</name>\n`;
        xml += `    <email>${escapeXml(r.email)}</email>\n`;
        xml += `    <phone>${escapeXml(r.phone)}</phone>\n`;
        xml += `    <status>${escapeXml(r.status)}</status>\n`;
        xml += `    <lastVisit>${escapeXml(r.lastVisit)}</lastVisit>\n`;
        xml += `    <totalAppointments>${escapeXml(r.totalAppointments)}</totalAppointments>\n`;
        xml += `    <totalSpent>${escapeXml(r.totalSpent)}</totalSpent>\n`;
        xml += `    <loyaltyPoints>${escapeXml(r.loyaltyPoints)}</loyaltyPoints>\n`;
        xml += "  </client>\n";
      });
      xml += "</clients>\n";

      downloadBlob(new Blob([xml], { type: "application/xml;charset=utf-8;" }), `clients-${dateSlug}.xml`);
    }

    setShowExportMenu(false);
  };

  const handleImportClick = () => {
    if (!fileInputRef.current) return;
    fileInputRef.current.value = "";
    fileInputRef.current.click();
  };

  const openImportErrorModal = (filename, message, raw = {}) => {
    setImportReport({
      created: 0,
      updated: 0,
      skipped_duplicates_in_file: 0,
      invalid_rows: 0,
      errors: [{ row: "", field: "file", message, raw }],
      filename: filename || "",
    });
    setIsImportReportOpen(true);
  };

  const handleImportFile = async (event) => {
    const inputEl = event?.target;
    const file = inputEl?.files?.[0];

    const resetInput = () => {
      if (inputEl) inputEl.value = "";
    };

    if (!file) return;

    const bizId = currentBusinessId || window.localStorage.getItem("current_business_id");
    if (!bizId) {
      openImportErrorModal(file?.name, "Nu există business selectat pentru import.");
      resetInput();
      return;
    }

    setIsImporting(true);

    try {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      let payloads = [];

      if (ext === "csv") {
        payloads = parseCsvClients(await file.text());
      } else if (ext === "xml") {
        payloads = parseXmlClients(await file.text());
      } else {
        openImportErrorModal(file?.name, "Format neacceptat. Încarcă .csv sau .xml.");
        return;
      }

      if (!payloads.length) {
        openImportErrorModal(file?.name, "Fișierul nu conține clienți valizi.");
        return;
      }

      const report = await importClients(payloads, { businessId: bizId });
      setImportReport({ ...(report || {}), filename: file?.name || "" });
      setIsImportReportOpen(true);

      const refreshed = await listClients({ businessId: bizId });
      setClients(Array.isArray(refreshed) ? refreshed : []);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Eroare la import. Verifică fișierul și încearcă din nou.";
      openImportErrorModal(file?.name, msg, err?.response?.data || {});
    } finally {
      setIsImporting(false);
      resetInput();
    }
  };

  // Guard: employee
  if (permissions?.isPureEmployee) {
    return (
      <div className="min-h-screen bg-background">
        <Header isCollapsed={sidebarCollapsed} onToggleSidebar={handleToggleSidebar} />
        <Sidebar isCollapsed={sidebarCollapsed} onToggle={handleToggleSidebar} />

        <main className={`page-top pb-20 lg:pb-6 transition-all duration-300 ${sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"}`}>
          <div className="p-6">
            <Breadcrumbs items={breadcrumbItems} className="mb-6" />
            <div className="bg-card border border-border rounded-lg p-8 text-center">
              <Icon name="Lock" size={40} className="mx-auto mb-4 text-muted-foreground" />
              <h1 className="text-2xl font-semibold mb-2">Nu aveți acces la managementul clienților</h1>
              <p className="text-muted-foreground">
                Contactați administratorul sau directorul de sucursală dacă considerați că ar trebui să aveți acces la această secțiune.
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header isCollapsed={sidebarCollapsed} onToggleSidebar={handleToggleSidebar} />
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={handleToggleSidebar} />

      <main className={`page-top pb-20 lg:pb-6 transition-all duration-300 ${sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"}`}>
        <div className="p-6">
          <Breadcrumbs items={breadcrumbItems} className="mb-6" />

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">{t("clients.title")}</h1>
              <p className="text-muted-foreground">
                {t("clients.pageSubtitle")}
              </p>
            </div>

            <div className="flex items-center space-x-0 md:space-x-3 mt-4 lg:mt-0 relative w-full justify-start lg:w-auto lg:justify-end">
              <div className="relative hidden md:block">
                <Button
                  variant="outline"
                  iconName="Download"
                  iconPosition="left"
                  onClick={() => setShowExportMenu((prev) => !prev)}
                >
                  Export
                </Button>

                {showExportMenu && (
                  <div className="absolute right-0 mt-2 w-40 bg-popover border border-border rounded-md shadow-lg z-20">
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 text-xs hover:bg-muted"
                      onClick={() => handleExport("csv")}
                    >
                      Export CSV
                    </button>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 text-xs hover:bg-muted"
                      onClick={() => handleExport("xml")}
                    >
                      Export XML
                    </button>
                  </div>
                )}
              </div>

              <Button
                variant="outline"
                iconName="Upload"
                iconPosition="left"
                onClick={handleImportClick}
                disabled={isImporting}
                className="hidden md:inline-flex"
              >
                {isImporting ? t("clients.importing") : t("common.import")}
              </Button>

              <Button
                variant="default"
                onClick={handleCreateClient}
                iconName="UserPlus"
                iconPosition="left"
              >
                {t("clients.newClient")}
              </Button>
            </div>
          </div>

          {canSeeStats && <ClientStats stats={computedStats} onOpenTotal={() => setShowClientListModal(true)} />}

          <ClientFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            serviceFilter={serviceFilter}
            onServiceFilterChange={setServiceFilter}
            sortBy={sortBy}
            onSortChange={setSortBy}
            onClearFilters={handleClearFilters}
          />

          <div className="flex items-center justify-between mb-6">
            <div className="text-sm text-muted-foreground">
              {t("clients.showingOf", { shown: filteredAndSortedClients?.length ?? 0, total: clients?.length ?? 0 })}
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center space-x-2">
                <Icon name="Users" size={16} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{t("clients.resultsCount", { count: filteredAndSortedClients?.length ?? 0 })}</span>
              </div>

              <div className="inline-flex rounded-lg border border-border overflow-hidden">
                <button
                  type="button"
                  onClick={() => setViewMode("cards")}
                  className={`px-3 py-1.5 text-xs flex items-center gap-1 ${
                    viewMode === "cards"
                      ? "bg-primary text-primary-foreground"
                      : "bg-card text-muted-foreground hover:bg-muted/60"
                  }`}
                >
                  <Icon name="LayoutGrid" size={14} />
                  <span>{t("clients.viewCards")}</span>
                </button>

                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={`px-3 py-1.5 text-xs flex items-center gap-1 border-l border-border ${
                    viewMode === "list"
                      ? "bg-primary text-primary-foreground"
                      : "bg-card text-muted-foreground hover:bg-muted/60"
                  }`}
                >
                  <Icon name="Rows" size={14} />
                  <span>{t("clients.viewList")}</span>
                </button>
              </div>
            </div>
          </div>

          {isFetching && (
            <div className="bg-card border border-border rounded-lg p-4 mb-6 text-sm text-muted-foreground">
              Se încarcă lista de clienți...
            </div>
          )}

          {!isFetching && filteredAndSortedClients?.length > 0 ? (
            viewMode === "cards" ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredAndSortedClients.map((client) => (
                  <ClientCard
                    key={client?.id}
                    client={client}
                    onBookAppointment={handleBookAppointment}
                    onEditClient={handleEditClient}
                    onViewProfile={handleViewProfile}
                  />
                ))}
              </div>
            ) : (
              <ClientTable
                clients={filteredAndSortedClients}
                onBookAppointment={handleBookAppointment}
                onEditClient={handleEditClient}
                onViewProfile={handleViewProfile}
              />
            )
          ) : !isFetching ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Users" size={32} className="text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">Nu s-au găsit clienți</h3>
              <p className="text-muted-foreground mb-6">Încearcă să modifici filtrele sau să adaugi un client nou.</p>
              <Button variant="default" onClick={handleCreateClient} iconName="UserPlus" iconPosition="left">
                Adaugă primul client
              </Button>
            </div>
          ) : null}
        </div>
      </main>

      <ImportReportModal
        isOpen={isImportReportOpen}
        report={importReport}
        onClose={() => setIsImportReportOpen(false)}
      />

      <input
        type="file"
        ref={fileInputRef}
        accept=".csv,.xml"
        className="hidden"
        onChange={handleImportFile}
      />

      {showClientForm && (
        <div
          className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-start justify-center pt-20 pb-8 px-4"
          onClick={handleClientFormOverlayClick}
        >
          <div className="w-full max-w-4xl max-h-[calc(100vh-6rem)] overflow-y-auto">
            <ClientForm
              client={editingClient}
              onSubmit={handleSubmitClient}
              onCancel={handleCloseClientForm}
              isLoading={isLoading}
            />
          </div>
        </div>
      )}

      {showBookingModal && selectedClient && (
        <AppointmentBookingModal
          client={selectedClient}
          isOpen={showBookingModal}
          onClose={() => {
            setShowBookingModal(false);
            setSelectedClient(null);
          }}
          onBook={handleBookingSubmit}
        />
      )}

      {profileClient && (
        <ClientProfileDrawer
          client={profileClient}
          isOpen={!!profileClient}
          onClose={() => setProfileClient(null)}
          onBookAppointment={handleBookAppointment}
          onEditClient={handleEditClient}
        />
      )}

      <OverlayListModal
        isOpen={showClientListModal}
        title={`Clienți (${clients?.length || 0})`}
        items={(clients || []).map((c) => ({
          id: c.id,
          title: c.name || c.fullName || c.email || c.phone || `Client #${c.id}`,
          subtitle: [c.phone, c.email].filter(Boolean).join(" · "),
          meta: [c.status, c.isVip ? "VIP" : null].filter(Boolean).join(" · "),
        }))}
        onClose={() => setShowClientListModal(false)}
        onItemClick={(it) => {
          const c = (clients || []).find((x) => String(x.id) === String(it.id));
          setShowClientListModal(false);
          if (c) setProfileClient(c);
        }}
      />
    </div>
  );
};

export default ClientManagement;
